<script lang="ts">
	import type { Snippet } from 'svelte';
	import ChevronRightIcon from '@lucide/svelte/icons/chevron-right';
	import PlusIcon from '@lucide/svelte/icons/plus';

	// Open WebUI `Sidebar/Section.svelte`: a 24px header row with the section
	// name in xs gray, a chevron that only appears on hover, and an optional
	// trailing action. The body collapses when the header is clicked.
	let {
		name,
		id,
		open = $bindable(true),
		onAdd,
		addLabel = 'Add',
		action,
		children,
		class: className = '',
	}: {
		name: string;
		id: string;
		open?: boolean;
		onAdd?: () => void;
		addLabel?: string;
		action?: Snippet;
		children: Snippet;
		class?: string;
	} = $props();
</script>

<div class="relative {className}" data-testid="sidebar-section" data-section={id}>
	<div class="flex h-6 w-full shrink-0 items-center justify-between pl-3.5 pr-1.5">
		<h2 class="flex h-full flex-1">
			<button
				type="button"
				class="group/section flex h-full flex-1 items-center gap-1 text-left text-xs text-gray-400 transition-colors duration-100 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
				aria-expanded={open}
				aria-controls="{id}-content"
				onclick={() => (open = !open)}
			>
				<span>{name}</span>
				<!-- Named group: the shadcn sidebar root is itself `.group`, so a bare
				     group-hover would show every chevron whenever the sidebar is hovered. -->
				<span
					class="flex opacity-0 transition-all duration-100 group-hover/section:opacity-100 group-focus-within/section:opacity-100"
					style="transform: rotate({open ? '90deg' : '0deg'})"
				>
					<ChevronRightIcon class="size-[0.6875rem]" strokeWidth={2.5} aria-hidden="true" />
				</span>
			</button>
		</h2>
		{#if action}
			{@render action()}
		{/if}
		{#if onAdd}
			<button
				type="button"
				class="flex size-7 items-center justify-center rounded-lg text-gray-300 transition-colors duration-100 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400"
				aria-label={addLabel}
				title={addLabel}
				onclick={onAdd}
			>
				<PlusIcon class="size-3.5" aria-hidden="true" />
			</button>
		{/if}
	</div>
	{#if open}
		<div id="{id}-content" class="w-full">
			{@render children()}
		</div>
	{/if}
</div>
